import * as Sentry from '@sentry/react';

/**
 * Sentry configuration for error tracking and performance monitoring
 */
// pending add rate limiting to avoid excessive events in case of error storms like loops


// Get environment variables
const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;
const SENTRY_ENVIRONMENT = import.meta.env.VITE_SENTRY_ENVIRONMENT || 'development';
const SENTRY_TRACES_SAMPLE_RATE = parseFloat(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE || '0.1');
const SENTRY_REPLAYS_SESSION_SAMPLE_RATE = parseFloat(import.meta.env.VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE || '0.1');
const SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE = parseFloat(import.meta.env.VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE || '1.0');

/**
 * Initialize Sentry for error tracking and performance monitoring
 * Should be called as early as possible in the application lifecycle
 */
export const initializeSentry = () => {
  // Only initialize Sentry if DSN is provided
  if (!SENTRY_DSN) {
    console.warn('[Sentry] DSN not provided. Sentry will not be initialized.');
    return;
  }

  Sentry.init({
    dsn: SENTRY_DSN,
    environment: SENTRY_ENVIRONMENT,

    // Integration with React Router for automatic navigation tracking
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        // Mask all text content and user input for privacy
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],

    // Performance Monitoring
    tracesSampleRate: SENTRY_TRACES_SAMPLE_RATE, // Capture 10% of transactions for performance monitoring

    // Session Replay
    replaysSessionSampleRate: SENTRY_REPLAYS_SESSION_SAMPLE_RATE, // 10% of sessions will be recorded
    replaysOnErrorSampleRate: SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE, // 100% of sessions with errors will be recorded

    // Configure which URLs to trace
    tracePropagationTargets: [
      'localhost',
      /^https:\/\/.*\.vaovaodata\.com/,
      /^https:\/\/api\.phyto\.vaovaodata\.com/,
    ],

    // Before sending events, you can modify or filter them
    beforeSend(event, hint) {
      // Filter out localhost errors in production
      if (SENTRY_ENVIRONMENT === 'production' && event.request?.url?.includes('localhost')) {
        return null;
      }

      // Log errors to console in development
      if (SENTRY_ENVIRONMENT === 'development') {
        console.group('[Sentry] Error captured');
        console.error(hint.originalException || hint.syntheticException);
        console.groupEnd();
      }

      return event;
    },

    // Ignore specific errors that are not actionable
    ignoreErrors: [
      // Browser extensions
      'top.GLOBALS',
      // Random plugins/extensions
      'originalCreateNotification',
      'canvas.contentDocument',
      'MyApp_RemoveAllHighlights',
      // Network errors that are expected
      'NetworkError',
      'Network request failed',
      // Auth0 specific errors that are handled
      'consent_required',
      'login_required',
    ],
  });

  console.log(`[Sentry] Initialized in ${SENTRY_ENVIRONMENT} environment`);
};

/**
 * Set the authenticated user context in Sentry
 * This helps identify which user experienced an error
 */
export const setSentryUser = (user: { id: string; email?: string; name?: string } | null) => {
  if (user) {
    Sentry.setUser({
      id: user.id,
      email: user.email,
      username: user.name,
    });
  } else {
    Sentry.setUser(null);
  }
};

/**
 * Add custom context to Sentry events
 */
export const setSentryContext = (context: string, data: Record<string, unknown>) => {
  Sentry.setContext(context, data);
};

/**
 * Add a breadcrumb to track user actions
 */
export const addSentryBreadcrumb = (breadcrumb: {
  message: string;
  category?: string;
  level?: 'fatal' | 'error' | 'warning' | 'info' | 'debug';
  data?: Record<string, unknown>;
}) => {
  Sentry.addBreadcrumb(breadcrumb);
};

/**
 * Manually capture an exception
 */
export const captureException = (error: Error, context?: Record<string, unknown>) => {
  if (context) {
    Sentry.withScope((scope) => {
      Object.entries(context).forEach(([key, value]) => {
        scope.setExtra(key, value);
      });
      Sentry.captureException(error);
    });
  } else {
    Sentry.captureException(error);
  }
};

/**
 * Manually capture a message
 */
export const captureMessage = (
  message: string,
  level: 'fatal' | 'error' | 'warning' | 'info' | 'debug' = 'info'
) => {
  Sentry.captureMessage(message, level);
};

/**
 * Export Sentry for direct access if needed
 */
export { Sentry };
